import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from '../../../../i18n';

export interface WordOrderPlayerProps {
  correctAnswer: string;
  onSubmit: (answer: string) => void;
  disabled?: boolean;
}

/** Default design-time cell size (px) before scale; scale shrinks the whole block to fit the container. */
const LETTER_MAX_PX = 52;
const GAP_PX = 6;

function letterMetricsForWordLen(n: number): { letterMaxPx: number; gapPx: number } {
  if (n >= 9) return { letterMaxPx: 40, gapPx: 4 };
  if (n >= 8) return { letterMaxPx: 44, gapPx: 5 };
  if (n >= 7) return { letterMaxPx: 48, gapPx: 5 };
  return { letterMaxPx: LETTER_MAX_PX, gapPx: GAP_PX };
}
/** Space between slot row and pool row (matches previous 1.5rem at 16px root). */
const ROW_GAP_PX = 24;

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function normalizeWord(s: string): string {
  return (s ?? '').trim().toUpperCase();
}

/**
 * WordOrder player component.
 *
 * Supports two interaction modes:
 * 1. **Tap-to-place** (works on mobile and desktop) — tap a pool letter to place
 *    it in the next empty slot. Tap a filled slot to select it for swapping or
 *    returning to the pool.
 * 2. **Drag-and-drop** (desktop) — HTML5 native drag.
 *
 * Layout uses a fixed design size then **scale()** so both letter rows always
 * fit the container width (no horizontal scroll on long words / small phones).
 *
 * Completion UX mirrors Puzzle: green flash + submit only when correct; red flash
 * and retry when the filled order is wrong.
 */
export const WordOrderPlayer: React.FC<WordOrderPlayerProps> = ({
  correctAnswer,
  onSubmit,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const letters = useMemo(() => shuffle(correctAnswer.split('')), [correctAnswer]);
  const [slots, setSlots] = useState<(string | null)[]>(() => Array(correctAnswer.length).fill(null));
  const [pool, setPool] = useState<string[]>(() => [...letters]);
  const [submitted, setSubmitted] = useState(false);
  const submittedRef = useRef(false);
  /** When all slots filled: green = correct (submit), red = wrong (retry). */
  const [borderFlash, setBorderFlash] = useState<'success' | 'wrong' | null>(null);
  const allFilledFlashRef = useRef(false);
  const submitScheduledRef = useRef(false);

  /** Selected filled slot index (for swap / return-to-pool). Pool letters are never “selected” — tap places into next empty. */
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [dragging, setDragging] = useState<{ source: 'pool' | 'slot'; index: number } | null>(null);

  /** Keep board in sync for rapid taps (avoid two letters mapping to the same empty slot). */
  const slotsRef = useRef(slots);
  const poolRef = useRef(pool);
  slotsRef.current = slots;
  poolRef.current = pool;

  const commitBoard = (nextSlots: (string | null)[], nextPool: string[]) => {
    slotsRef.current = nextSlots;
    poolRef.current = nextPool;
    setSlots(nextSlots);
    setPool(nextPool);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const n = correctAnswer.length;
  const { letterMaxPx, gapPx } = useMemo(() => letterMetricsForWordLen(n), [n]);
  const contentWidth = useMemo(
    () => (n <= 0 ? 0 : n * letterMaxPx + (n - 1) * gapPx),
    [n, letterMaxPx, gapPx]
  );

  const rowHeight = letterMaxPx + 8;
  const innerHeightUnscaled = rowHeight * 2 + ROW_GAP_PX;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const readContentWidth = () => {
      const cs = getComputedStyle(el);
      const pl = parseFloat(cs.paddingLeft) || 0;
      const pr = parseFloat(cs.paddingRight) || 0;
      const w = el.clientWidth - pl - pr;
      if (w > 0) setContainerWidth(w);
    };

    readContentWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', readContentWidth);
      return () => window.removeEventListener('resize', readContentWidth);
    }

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w != null && w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = useMemo(() => {
    if (contentWidth <= 0 || containerWidth <= 0) return 1;
    return Math.min(1, containerWidth / contentWidth);
  }, [contentWidth, containerWidth]);

  const scaledHeight = innerHeightUnscaled * scale;

  const isCorrectOrder = useMemo(() => {
    if (pool.length > 0 || slots.some((s) => s === null)) return false;
    return normalizeWord(slots.join('')) === normalizeWord(correctAnswer);
  }, [slots, pool.length, correctAnswer]);

  // When all slots filled: blink green (correct) or red (wrong). Submit only on success
  // immediately (same as Puzzle) so effect cleanup / parent re-renders can't cancel the send.
  useEffect(() => {
    const allFilled = pool.length === 0 && slots.every((s) => s !== null);
    if (!allFilled) {
      allFilledFlashRef.current = false;
      submitScheduledRef.current = false;
      return;
    }
    // Only skip when we already showed the *wrong* flash and we're still not correct.
    if (allFilledFlashRef.current && !isCorrectOrder) return;
    allFilledFlashRef.current = true;
    setBorderFlash(isCorrectOrder ? 'success' : 'wrong');
    const t1 = setTimeout(() => {
      setBorderFlash(null);
    }, 1000);
    if (isCorrectOrder && !submittedRef.current && !disabled && !submitScheduledRef.current) {
      submitScheduledRef.current = true;
      submittedRef.current = true;
      setSubmitted(true);
      onSubmit(slots.join(''));
    }
    return () => clearTimeout(t1);
  }, [slots, pool.length, isCorrectOrder, disabled, onSubmit]);

  const isInactive = disabled || submitted;

  const handleTapPool = (index: number) => {
    if (isInactive) return;

    const curSlots = slotsRef.current;
    const curPool = poolRef.current;

    // Slot selected → swap that slot letter with this pool letter
    if (selectedSlot != null) {
      const poolLetter = curPool[index];
      const slotLetter = curSlots[selectedSlot];
      if (slotLetter == null || poolLetter == null) return;
      const nextSlots = [...curSlots];
      nextSlots[selectedSlot] = poolLetter;
      const nextPool = curPool.filter((_, i) => i !== index);
      nextPool.push(slotLetter);
      commitBoard(nextSlots, nextPool);
      setSelectedSlot(null);
      return;
    }

    // Default: place tapped pool letter into the next empty slot
    const emptyIndex = curSlots.findIndex((s) => s === null);
    const letter = curPool[index];
    if (emptyIndex === -1 || letter == null) return;
    const nextSlots = [...curSlots];
    nextSlots[emptyIndex] = letter;
    commitBoard(
      nextSlots,
      curPool.filter((_, i) => i !== index)
    );
    setSelectedSlot(null);
  };

  const handleTapSlot = (slotIndex: number) => {
    if (isInactive) return;

    const curSlots = slotsRef.current;
    const curPool = poolRef.current;

    if (selectedSlot != null) {
      if (selectedSlot === slotIndex) {
        setSelectedSlot(null);
        return;
      }
      const nextSlots = [...curSlots];
      const a = curSlots[selectedSlot];
      const b = curSlots[slotIndex];
      nextSlots[selectedSlot] = b;
      nextSlots[slotIndex] = a;
      commitBoard(nextSlots, curPool);
      setSelectedSlot(null);
      return;
    }

    if (curSlots[slotIndex]) {
      setSelectedSlot(slotIndex);
    }
  };

  const handleTapPoolArea = () => {
    if (isInactive) return;
    if (selectedSlot != null) {
      const curSlots = slotsRef.current;
      const letter = curSlots[selectedSlot];
      if (letter) {
        const nextSlots = [...curSlots];
        nextSlots[selectedSlot] = null;
        commitBoard(nextSlots, [...poolRef.current, letter]);
      }
      setSelectedSlot(null);
    }
  };

  const handleDragStart = (source: 'pool' | 'slot', index: number) => {
    if (isInactive) return;
    setDragging({ source, index });
    setSelectedSlot(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnSlot = (slotIndex: number) => {
    if (!dragging || isInactive) return;
    const curSlots = slotsRef.current;
    const curPool = poolRef.current;
    if (dragging.source === 'slot') {
      const nextSlots = [...curSlots];
      const a = curSlots[dragging.index];
      const b = nextSlots[slotIndex];
      nextSlots[dragging.index] = b;
      nextSlots[slotIndex] = a;
      commitBoard(nextSlots, curPool);
    } else {
      const letter = curPool[dragging.index];
      if (letter == null) {
        setDragging(null);
        return;
      }
      const existing = curSlots[slotIndex];
      const nextPool = curPool.filter((_, i) => i !== dragging.index);
      if (existing) nextPool.push(existing);
      const nextSlots = [...curSlots];
      nextSlots[slotIndex] = letter;
      commitBoard(nextSlots, nextPool);
    }
    setDragging(null);
  };

  const handleDropOnPool = () => {
    if (!dragging || isInactive) return;
    if (dragging.source === 'slot') {
      const curSlots = slotsRef.current;
      const letter = curSlots[dragging.index];
      if (letter) {
        const nextSlots = [...curSlots];
        nextSlots[dragging.index] = null;
        commitBoard(nextSlots, [...poolRef.current, letter]);
      }
    }
    setDragging(null);
  };

  const handleDragEnd = () => {
    setDragging(null);
  };

  const fontSize = letterMaxPx * 0.52;
  const gap = gapPx;

  const isSelectedSlot = (index: number) => selectedSlot === index;

  const selectedBorder = '2px solid #34D399';
  const selectedShadow = '0 0 8px rgba(52, 211, 153, 0.5)';
  const successBorder = '2px solid #22c55e';
  const successBackground = 'rgba(34, 197, 94, 0.22)';
  const wrongBorder = '2px solid #ef4444';
  const wrongBackground = 'rgba(239, 68, 68, 0.18)';
  const emptySlotBorder = '2px dashed rgba(var(--color-secondary-rgb), 0.6)';
  const emptySlotBackground = 'linear-gradient(135deg, rgba(var(--color-secondary-rgb), 0.08) 0%, rgba(var(--color-primary-rgb), 0.06) 100%)';
  const filledSlotBackground = 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.18) 0%, rgba(var(--color-secondary-rgb), 0.12) 100%)';
  const poolLetterBackground = 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.14) 0%, rgba(var(--color-secondary-rgb), 0.18) 100%)';

  const borderBlinkStyle = `
    @keyframes wordOrderBorderSuccess {
      0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.3); }
      50% { box-shadow: 0 0 12px 3px rgba(52, 211, 153, 0.75); }
    }
    @keyframes wordOrderBorderWrong {
      0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.3); }
      50% { box-shadow: 0 0 12px 3px rgba(239, 68, 68, 0.7); }
    }
  `;

  const letterBoxStyle = (opts: {
    border: string;
    background: string;
    showShadow: boolean;
    cursor: 'default' | 'pointer';
    animation?: string;
  }) => ({
    width: letterMaxPx,
    minWidth: letterMaxPx,
    height: letterMaxPx + 8,
    border: opts.border,
    borderRadius: 8,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    fontSize: `${fontSize}px`,
    fontWeight: 'bold' as const,
    background: opts.background,
    cursor: opts.cursor,
    boxShadow: opts.showShadow ? selectedShadow : 'none',
    transition: 'background 0.15s, border 0.15s, box-shadow 0.15s',
    animation: opts.animation,
  });
  const letterBoxClassName = 'player-surface-tile';

  const slotFlashBorder =
    borderFlash === 'success' ? successBorder : borderFlash === 'wrong' ? wrongBorder : null;
  const slotFlashBackground =
    borderFlash === 'success'
      ? successBackground
      : borderFlash === 'wrong'
        ? wrongBackground
        : null;
  const slotFlashAnimation =
    borderFlash === 'success'
      ? 'wordOrderBorderSuccess 1s ease'
      : borderFlash === 'wrong'
        ? 'wordOrderBorderWrong 1s ease'
        : undefined;

  if (n === 0) {
    return (
      <div
        ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 520,
        minWidth: 0,
        alignSelf: 'stretch',
        margin: '0 auto',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: 'min(520px, 100%)',
        minWidth: 0,
        alignSelf: 'stretch',
        margin: '0 auto',
        padding: '1rem max(0.5rem, env(safe-area-inset-right)) 1rem max(0.5rem, env(safe-area-inset-left))',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        overflow: 'hidden',
        overflowX: 'clip',
        boxSizing: 'border-box',
      }}
    >
      <style>{borderBlinkStyle}</style>
      {/* Scaled block: slots + pool rows share one transform so width always fits container */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          minHeight: scaledHeight,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: contentWidth * scale,
            height: scaledHeight,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              width: contentWidth,
              transform: `translateX(-50%) scale(${scale})`,
              transformOrigin: 'top center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${gap}px`,
                flexWrap: 'nowrap',
                marginBottom: ROW_GAP_PX,
                minHeight: rowHeight,
                width: contentWidth,
              }}
            >
              {slots.map((letter, i) => (
                <div
                  key={`slot-${i}`}
                  data-slot-index={i}
                  className={letterBoxClassName}
                  onClick={() => handleTapSlot(i)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropOnSlot(i)}
                  style={{
                    ...letterBoxStyle({
                      border:
                        slotFlashBorder ??
                        (isSelectedSlot(i) ? selectedBorder : emptySlotBorder),
                      background: letter
                        ? slotFlashBackground ??
                          (isSelectedSlot(i)
                            ? 'rgba(52, 211, 153, 0.25)'
                            : filledSlotBackground)
                        : emptySlotBackground,
                      showShadow: isSelectedSlot(i) && !borderFlash,
                      cursor: isInactive ? 'default' : 'pointer',
                      animation: letter ? slotFlashAnimation : undefined,
                    }),
                    color: 'var(--text-primary)',
                  }}
                  draggable={!!letter && !isInactive}
                  onDragStart={() => letter && handleDragStart('slot', i)}
                  onDragEnd={handleDragEnd}
                >
                  {letter ?? ''}
                </div>
              ))}
            </div>

            <div
              onClick={handleTapPoolArea}
              onDragOver={handleDragOver}
              onDrop={handleDropOnPool}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: `${gap}px`,
                flexWrap: 'nowrap',
                minHeight: rowHeight,
                width: contentWidth,
              }}
            >
              {pool.map((letter, i) => (
                <div
                  key={`pool-${i}-${letter}`}
                  className={letterBoxClassName}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTapPool(i);
                  }}
                  draggable={!isInactive}
                  onDragStart={() => handleDragStart('pool', i)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...letterBoxStyle({
                      border: '2px solid var(--color-primary)',
                      background: poolLetterBackground,
                      showShadow: false,
                      cursor: isInactive ? 'default' : 'pointer',
                    }),
                    color: 'var(--text-primary)',
                    touchAction: 'none',
                  }}
                >
                  {letter}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!submitted && pool.length > 0 && (
        <div className="bonus-player-hint-wrap">
          <p className="bonus-player-hint" style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            {t('bonus.player.wordOrderHint')}
          </p>
        </div>
      )}
    </div>
  );
};
